import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import update from 'immutability-helper'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd'
import { getEmptyImage, HTML5Backend } from 'react-dnd-html5-backend'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import {
  createDimensionField,
  getMovedFromRanks
} from '../../helpers/culture-ranking'
import GripIcon from '../grip-icon'
import Drawer from '../tailwind/Drawer'

const DRAG_TYPE = 'dreamteam-card'

// Ein Swap darf erst ausloesen, wenn der Cursor die Mitte der Nachbarzeile um
// diesen Anteil ihrer Hoehe ueberschritten hat. Ohne die Deadzone kippt der
// Swap zurueck, sobald sich das Layout durch unterschiedlich hohe Zeilen
// verschiebt (Beschreibungen brechen unter ~700px um).
const SWAP_DEADZONE_RATIO = 0.15

// Der Browser feuert dragover auch bei stillstehender Maus (~alle 350ms). Erst
// ab dieser Distanz zum letzten Swap darf erneut umsortiert werden.
const MIN_MOVE_BETWEEN_SWAPS = 4

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false

/**
 * Zeigt, dass dieser Wert gegenueber dem Unternehmensprofil verschoben wurde,
 * und woher er kam. Bewusst blau und nicht gruen/rot: alle zehn Werte sind laut
 * Copy positiv, oben und unten sind hier keine Wertung, sondern nur Reihenfolge.
 * Grau faellt weg, das liest auf diesem Screen als deaktiviert.
 */
export const MovedFromChip = ({ rank, fromRank, inverted }) => {
  const { t } = useTranslation()
  const Icon = rank < fromRank ? ArrowUpIcon : ArrowDownIcon

  return (
    <span
      className={clsx(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        inverted
          ? 'bg-white/15 text-white ring-white/30'
          : 'bg-blue-50 text-blue-700 ring-blue-600/20'
      )}
    >
      <Icon aria-hidden='true' className='h-3 w-3' />
      {/* Sichtbar knapp, fuer Screenreader ein ganzer Satz. Den aktuellen Rang
          kuendigt direkt davor schon rank_label an. */}
      <span aria-hidden='true'>
        {t('create_career.cultural_fit.dreamteam.moved_from', {
          rank: fromRank
        })}
      </span>
      <span className='sr-only'>
        {t('create_career.cultural_fit.dreamteam.moved_from_label', {
          rank: fromRank
        })}
      </span>
    </span>
  )
}

MovedFromChip.propTypes = {
  rank: PropTypes.number.isRequired,
  fromRank: PropTypes.number.isRequired,
  inverted: PropTypes.bool
}

/**
 * Linke Kartenhaelfte. Wird von der Zeile und vom Drag-Layer geteilt, damit
 * beide garantiert identisch aussehen (invertiert nur ueber `inverted`).
 */
const CardContent = ({
  rank,
  title,
  description,
  inverted,
  infoSlot,
  movedFromRank
}) => (
  <div className='flex min-w-0 items-center gap-2'>
    <GripIcon
      className={clsx(
        'h-4 w-4 shrink-0 transition-colors',
        inverted ? 'text-blue-200' : 'text-gray-300 group-hover:text-gray-500'
      )}
    />
    <div
      aria-hidden='true'
      className={clsx(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2',
        inverted ? 'border-white' : 'border-blue-600'
      )}
    >
      <span
        className={clsx(
          'text-xs font-semibold',
          inverted ? 'text-white' : 'text-blue-700'
        )}
      >
        {String(rank).padStart(2, '0')}
      </span>
    </div>
    <div className='min-w-0'>
      <div className='flex flex-wrap items-center gap-1'>
        <span
          className={clsx(
            'text-xs font-semibold uppercase tracking-wide',
            inverted ? 'text-white' : 'text-gray-900'
          )}
        >
          {title}
        </span>
        {infoSlot}
        {typeof movedFromRank === 'number' && (
          <MovedFromChip
            rank={rank}
            fromRank={movedFromRank}
            inverted={inverted}
          />
        )}
      </div>
      <p
        className={clsx('text-xs', inverted ? 'text-blue-50' : 'text-gray-600')}
      >
        {description}
      </p>
    </div>
  </div>
)

CardContent.propTypes = {
  rank: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  inverted: PropTypes.bool,
  infoSlot: PropTypes.node,
  movedFromRank: PropTypes.number
}

/**
 * Frei schwebende Karte am Cursor. Eigene Komponente, weil useDragLayer bei
 * jedem dragover re-rendert - im Parent wuerden alle 10 Zeilen mitrendern.
 */
const DreamteamDragLayer = () => {
  const [reduceMotion] = useState(prefersReducedMotion)

  const { isDragging, item, itemType, sourceOffset, dragIndex } = useDragLayer(
    (monitor) => ({
      isDragging: monitor.isDragging(),
      item: monitor.getItem(),
      itemType: monitor.getItemType(),
      sourceOffset: monitor.getSourceClientOffset(),
      // hover() mutiert item.index in-place, die Objektreferenz bleibt gleich.
      // Der Collector vergleicht shallow - deshalb als eigenes Skalar.
      dragIndex: monitor.getItem()?.index ?? null
    })
  )

  if (!isDragging || itemType !== DRAG_TYPE || !item || !sourceOffset) {
    return null
  }

  const transform = [
    `translate3d(${sourceOffset.x}px, ${sourceOffset.y}px, 0)`,
    reduceMotion ? null : 'rotate(1.5deg)'
  ]
    .filter(Boolean)
    .join(' ')

  return createPortal(
    // pointer-events-none ist tragend: ein fixed-Overlay unter dem Cursor
    // wuerde dragover schlucken und Drops komplett brechen.
    <div
      className='pointer-events-none fixed left-0 top-0 z-50'
      style={{
        transform,
        width: item.width || undefined,
        height: item.height || undefined,
        willChange: 'transform'
      }}
    >
      <div className='flex h-full w-full items-center justify-between overflow-hidden rounded-lg bg-blue-600 px-3 py-2 shadow-xl'>
        <CardContent
          rank={(dragIndex ?? 0) + 1}
          title={item.title}
          description={item.description}
          movedFromRank={item.movedFromRank}
          inverted
          infoSlot={
            <InformationCircleIcon
              aria-hidden='true'
              className='size-5 shrink-0 text-white'
            />
          }
        />
        <div
          aria-hidden='true'
          className='ml-4 flex shrink-0 flex-row items-center gap-1'
        >
          <span className='flex h-9 w-9 items-center justify-center'>
            <ChevronUpIcon className='h-3.5 w-3.5 text-blue-200' />
          </span>
          <span className='flex h-9 w-9 items-center justify-center'>
            <ChevronDownIcon className='h-3.5 w-3.5 text-blue-200' />
          </span>
        </div>
      </div>
    </div>,
    document.body
  )
}

const DreamteamCard = ({
  id,
  index,
  total,
  title,
  description,
  moveCard,
  onDragBegin,
  onDragFinish,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onShowDetails,
  movedFromRank
}) => {
  const { t } = useTranslation()
  const cardRef = useRef(null)
  // Merkt sich, ob der pointerdown auf einem interaktiven Kind lag.
  // pointerdown laeuft immer vor dragstart, das Flag ist in canDrag also
  // verlaesslich lesbar.
  const noDragRef = useRef(false)

  const [{ handlerId }, drop] = useDrop({
    accept: DRAG_TYPE,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() }
    },
    drop: () => ({ dropped: true }),
    // WICHTIG: hover() misst bei jedem dragover getBoundingClientRect(). Der
    // Platzhalter-Zustand (isDragging) muss deshalb exakt dieselbe Boxhoehe
    // behalten - 1px border, unveraendertes Padding, Kinder nur `invisible`.
    hover(item, monitor) {
      if (!cardRef.current) return

      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return

      if (
        typeof item.lastSwapY === 'number' &&
        Math.abs(clientOffset.y - item.lastSwapY) < MIN_MOVE_BETWEEN_SWAPS
      ) {
        return
      }

      const hoverBoundingRect = cardRef.current.getBoundingClientRect()
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      const deadzone = hoverBoundingRect.height * SWAP_DEADZONE_RATIO

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY + deadzone) {
        return
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY - deadzone) {
        return
      }

      moveCard(dragIndex, hoverIndex)
      item.index = hoverIndex
      item.lastSwapY = clientOffset.y
    }
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: DRAG_TYPE,
    canDrag: () => !noDragRef.current,
    item: () => {
      const rect = cardRef.current?.getBoundingClientRect()
      onDragBegin()
      return {
        id,
        index,
        startIndex: index,
        title,
        description,
        // Am Drag-Start eingefroren. Der Baseline-Rang aendert sich waehrend
        // des Ziehens ohnehin nicht.
        movedFromRank,
        width: rect?.width ?? 0,
        height: rect?.height ?? 0
      }
    },
    end: (item, monitor) => onDragFinish(item, monitor.didDrop()),
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  })

  drag(drop(cardRef))

  useEffect(() => {
    // Der native Ghost ist ein statisches Bitmap vom dragstart und laesst sich
    // danach nicht umstylen -> leeres Bild + DreamteamDragLayer.
    // captureDraggingState verhindert, dass isDragging einen Tick zu spaet kommt.
    preview(getEmptyImage(), { captureDraggingState: true })
  }, [preview])

  const handlePointerDownCapture = (event) => {
    noDragRef.current = Boolean(event.target.closest?.('[data-no-drag]'))
  }

  const handleDragStartCapture = (event) => {
    // canDrag stoppt nur den react-dnd-State. Der Browser wuerde den nativen
    // Drag trotzdem starten, weil draggable="true" auf der Karte sitzt.
    if (noDragRef.current) event.preventDefault()
  }

  const arrowClassName =
    'flex h-9 w-9 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 aria-disabled:cursor-default aria-disabled:opacity-30 aria-disabled:hover:bg-transparent aria-disabled:hover:text-gray-400'

  return (
    <li
      ref={cardRef}
      data-handler-id={handlerId}
      onPointerDownCapture={handlePointerDownCapture}
      onDragStartCapture={handleDragStartCapture}
      className={clsx(
        'group mb-1.5 flex select-none items-center justify-between rounded-lg border px-3 py-2',
        isDragging
          ? 'border-dashed border-blue-400 bg-blue-50'
          : 'cursor-grab bg-white transition-shadow hover:shadow-md active:cursor-grabbing',
        !isDragging &&
          (typeof movedFromRank === 'number'
            ? 'border-blue-200 hover:border-blue-300'
            : 'border-gray-200 hover:border-gray-300')
      )}
    >
      <span className='sr-only'>
        {t('create_career.cultural_fit.dreamteam.rank_label', {
          rank: index + 1,
          total
        })}
      </span>

      <div className={clsx('flex min-w-0 flex-1', isDragging && 'invisible')}>
        <CardContent
          rank={index + 1}
          title={title}
          description={description}
          movedFromRank={movedFromRank}
          infoSlot={
            <button
              type='button'
              data-no-drag
              onClick={onShowDetails}
              aria-label={t(
                'create_career.cultural_fit.dreamteam.show_details',
                { title }
              )}
              className='-m-1.5 shrink-0 rounded p-1.5 text-gray-900 transition-colors hover:text-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
            >
              <InformationCircleIcon aria-hidden='true' className='size-5' />
            </button>
          }
        />
      </div>

      <div
        data-no-drag
        className={clsx(
          'ml-4 flex shrink-0 flex-row items-center gap-1',
          isDragging && 'invisible'
        )}
      >
        {/* aria-disabled statt disabled: ein disabled werdendes Element
            verliert den Fokus an <body> und wirft Tastaturnutzer an den
            Seitenanfang, sobald ein Item auf Rang 1 bzw. 10 landet. */}
        <button
          type='button'
          onClick={isFirst ? undefined : onMoveUp}
          aria-disabled={isFirst}
          aria-label={t('create_career.cultural_fit.dreamteam.move_up', {
            title
          })}
          className={arrowClassName}
        >
          <ChevronUpIcon aria-hidden='true' className='h-3.5 w-3.5' />
        </button>
        <button
          type='button'
          onClick={isLast ? undefined : onMoveDown}
          aria-disabled={isLast}
          aria-label={t('create_career.cultural_fit.dreamteam.move_down', {
            title
          })}
          className={arrowClassName}
        >
          <ChevronDownIcon aria-hidden='true' className='h-3.5 w-3.5' />
        </button>
      </div>
    </li>
  )
}

DreamteamCard.propTypes = {
  id: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  moveCard: PropTypes.func.isRequired,
  onDragBegin: PropTypes.func.isRequired,
  onDragFinish: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  isFirst: PropTypes.bool.isRequired,
  isLast: PropTypes.bool.isRequired,
  onShowDetails: PropTypes.func.isRequired,
  movedFromRank: PropTypes.number
}

const DreamteamList = ({ items, setItems, dimensions, baselineRanks }) => {
  const { t } = useTranslation()
  const [activeKey, setActiveKey] = useState(null)
  const [announcement, setAnnouncement] = useState('')

  const itemsRef = useRef(items)
  itemsRef.current = items
  const snapshotRef = useRef(null)
  const isDraggingRef = useRef(false)

  // Waehrend eines Drags eingefroren. hover() misst bei jedem dragover
  // getBoundingClientRect(), und ein Chip, der mitten im Zug erscheint oder
  // verschwindet, laesst die Titel-Zeile umbrechen und die gemessene Box unter
  // dem Cursor wachsen - genau das Flappen, gegen das SWAP_DEADZONE_RATIO und
  // die `invisible`-Platzhalter schon arbeiten. Nebeneffekt und Gewinn: die
  // Chips flackern beim Ziehen nicht.
  // Endet der Drop auf dem Startindex, gibt es kein Re-Render, dann ist die
  // Reihenfolge aber unveraendert und die eingefrorene Map weiterhin korrekt.
  // Schreiben im Render wie itemsRef eine Zeile darueber.
  const movedFromRanksRef = useRef(null)
  if (!isDraggingRef.current) {
    movedFromRanksRef.current = getMovedFromRanks(items, baselineRanks)
  }
  const movedFromRanks = movedFromRanksRef.current

  const moveCard = useCallback(
    (dragIndex, hoverIndex) => {
      setItems((prev) =>
        update(prev, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, prev[dragIndex]]
          ]
        })
      )
    },
    [setItems]
  )

  const getDimensionField = createDimensionField({ dimensions, t })

  const announceRank = (title, rank) =>
    setAnnouncement(
      t('create_career.cultural_fit.dreamteam.rank_announcement', {
        title,
        rank,
        total: itemsRef.current.length
      })
    )

  const handleDragBegin = () => {
    snapshotRef.current = itemsRef.current
    isDraggingRef.current = true
  }

  const handleDragFinish = (item, didDrop) => {
    isDraggingRef.current = false

    // hover() sortiert live um, es gibt aber keinen impliziten Rollback:
    // Esc oder ein Release ausserhalb der Liste wuerde die Reihenfolge sonst
    // still committen.
    if (!didDrop) {
      if (snapshotRef.current) setItems(snapshotRef.current)
      snapshotRef.current = null
      return
    }

    snapshotRef.current = null
    if (item.index !== item.startIndex) announceRank(item.title, item.index + 1)
  }

  const moveByButton = (index, direction) => {
    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex > itemsRef.current.length - 1) return

    const title = getDimensionField(itemsRef.current[index], 'title')
    moveCard(index, nextIndex)
    announceRank(title, nextIndex + 1)
  }

  // Ohne dieses Drop-Target waere ein Release in den 6px mb-1.5 zwischen zwei
  // Karten ein didDrop() === false und wuerde zurueckgerollt.
  const [, dropContainer] = useDrop({
    accept: DRAG_TYPE,
    drop: (item, monitor) => (monitor.didDrop() ? undefined : { dropped: true })
  })

  const activeTitle = activeKey ? getDimensionField(activeKey, 'title') : ''
  const activeDetail = activeKey ? getDimensionField(activeKey, 'detail') : ''

  return (
    <div>
      <p className='mb-3 text-sm text-gray-600'>
        {t('create_career.cultural_fit.dreamteam.instruction')}
      </p>

      <DreamteamDragLayer />

      <ol ref={dropContainer}>
        {items.map((key, index) => (
          // key ist tragend: keyed auf den Dimension-String aendert ein Reorder
          // nur die index-Prop. Mit key={index} wuerde die Komponente mitten im
          // Drag remounten und der Drag stirbt.
          <DreamteamCard
            key={key}
            id={key}
            index={index}
            total={items.length}
            title={getDimensionField(key, 'title')}
            description={getDimensionField(key, 'description')}
            moveCard={moveCard}
            onDragBegin={handleDragBegin}
            onDragFinish={handleDragFinish}
            onMoveUp={() => moveByButton(index, -1)}
            onMoveDown={() => moveByButton(index, 1)}
            isFirst={index === 0}
            isLast={index === items.length - 1}
            movedFromRank={movedFromRanks[key]}
            onShowDetails={() => {
              if (isDraggingRef.current) return
              setActiveKey(key)
            }}
          />
        ))}
      </ol>

      <div aria-live='polite' aria-atomic='true' className='sr-only'>
        {announcement}
      </div>

      <Drawer
        open={activeKey !== null}
        onClose={() => setActiveKey(null)}
        title={activeTitle}
        content={activeDetail}
        backdrop
      />
    </div>
  )
}

DreamteamList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  setItems: PropTypes.func.isRequired,
  dimensions: PropTypes.object,
  baselineRanks: PropTypes.object
}

const DreamteamRanking = (props) => (
  <DndProvider backend={HTML5Backend}>
    <DreamteamList {...props} />
  </DndProvider>
)

export default DreamteamRanking
